<template>
  <div id="textblock" :class="classes">
    <MyIcon
      v-if="icon"
      style="margin-block-end: var(--spacing-sm)"
      :name="`${icon}`"
      :is-svg="true"
      :size="`${iconsize}`"
    />
    <!-- Type badge hidden temporarily -->
    <!-- <div v-if="shouldShowTags" class="tags">
      <MyButton
        type="subtle"
        size="xs"
        :label="typeLabel"
        :customBgColor="typeColorSubtle"
        :customTextColor="typeColor"
      />
    </div> -->
    <p v-else-if="eyebrow" class="eyebrow subtle">{{ eyebrow }}</p>
    <a
      v-if="title && titleRoute && (isExternalTitleLink || isExternal)"
      :href="titleRoute"
      target="_blank"
      rel="noopener noreferrer"
      class="title-link"
    >
      <DynamicText :as="as" :text="title" :attrs="{ class: 'title' }" />
    </a>
    <router-link v-else-if="title && titleRoute" :to="titleRoute" class="title-link">
      <DynamicText :as="as" :text="title" :attrs="{ class: 'title' }" />
    </router-link>
    <DynamicText
      v-else-if="title"
      :as="as"
      tabIndex="0"
      :text="title"
      :isHtml="isHtml"
      :attrs="{ class: 'title' }"
    />
    <DynamicText
      v-if="description"
      isHtml
      tabIndex="0"
      :text="description"
      :attrs="{ class: 'description' }"
    />
    <!-- Content tags at bottom -->
    <!-- `subtle` matches the muted treatment of the eyebrow (p.eyebrow.subtle):
         the inner <p> keeps body color while .subtle drops opacity to 0.75, so
         the meta row (date · read time · tags) renders identically muted. -->
    <div
      v-if="shouldShowTags && ((tags && tags.length) || readTime || date || isExternal || complete)"
      class="tags tags--content subtle"
    >
      <span v-if="isExternal" class="tag-label tag-label--external">
        <p>External &#8599;</p>
      </span>
      <span v-if="date" class="tag-label tag-label--date">
        <p>{{ date }}</p>
      </span>
      <span v-if="readTime" class="tag-label tag-label--read-time">
        <p>{{ readTime }}</p>
      </span>
      <span v-if="complete" class="tag-label tag-label--complete">
        <p>&#10003; Complete</p>
      </span>
      <span v-for="tag in tags" :key="tag" class="tag-label" @click="$emit('tag-click', tag)"
        ><p>{{ tag }}</p></span
      >
    </div>
    <!-- <TextLink
      v-if="route"
      :label="label"
      :route="route ? `${route}` : undefined"
      :link="link ? `${link}` : undefined"
    />
    <MyButton
      id="btn"
      v-if="route"
      :label="label"
      :route="route"
      type="textlink"
    /> -->
    <MyButton
      id="btn"
      :type="btntype"
      v-if="(route || btnroute || link) && label"
      :label="label"
      :route="link ? undefined : route || btnroute"
      :link="link"
    />
  </div>
</template>

<script>
import MyButton from '../../Button/Button.vue';
import DynamicText from '../DynamicText.vue';
import MyIcon from '../../Icon.vue';
export default {
  name: 'TextBlock',
  components: {
    MyButton,
    // TextLink,
    DynamicText,
    MyIcon,
  },
  props: {
    icon: {
      type: String,
    },
    iconsize: {
      type: String,
      default: '64',
    },
    alt: {
      type: String,
    },
    eyebrow: {
      type: String,
      required: false,
    },
    as: {
      default: 'h3',
      type: String,
      required: false,
    },
    btntype: {
      type: String,
    },
    title: {
      type: String,
      default: '',
      required: false,
    },

    description: {
      type: String,
      default: '',
      required: false,
    },
    // blockquote: {
    //   type: String,
    //   default: "",
    //   required: false,
    // },
    center: {
      type: Boolean,
      default: false,
    },
    clamped: {
      type: Boolean,
      default: false,
    },
    reversed: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
      required: false,
    },
    route: {
      type: String,
      required: false,
    },
    btnroute: {
      type: String,
      required: false,
    },
    link: {
      default: '',
      type: String,
    },
    titleRoute: {
      type: String,
      required: false,
    },
    tags: {
      type: Array,
      default: () => [],
      required: false,
    },
    cardType: {
      type: String,
      default: null,
      required: false,
    },
    readTime: {
      type: String,
      default: '',
      required: false,
    },
    date: {
      type: String,
      default: '',
      required: false,
    },
    complete: {
      type: Boolean,
      default: false,
      required: false,
    },
    isExternal: {
      type: Boolean,
      default: false,
      required: false,
    },
    isHtml: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    shouldShowTags() {
      // Show tags only when used in a card context (cardType is explicitly provided)
      return this.cardType !== null;
    },
    classes() {
      return {
        'textblock-align': true,
        'textblock-align--center': this.center,
        'textblock-align--left': !this.center,

        'textblock--clamped': this.clamped,
        'textblock--normal': !this.clamped,

        reversed: this.reversed,
      };
    },
    isExternalTitleLink() {
      return (
        this.titleRoute &&
        (this.titleRoute.startsWith('http://') || this.titleRoute.startsWith('https://'))
      );
    },
    typeColor() {
      const typeColorMap = {
        article: 'var(--text)',
        tool: 'var(--text)',
        'case-study': 'var(--text)',
        'design-project': 'var(--text)',
      };
      return typeColorMap[this.cardType] || '#0066b3';
    },
    typeColorSubtle() {
      const subtleColorMap = {
        article: 'var(--background-darker)',
        tool: 'var(--background-darker)',
        'case-study': 'var(--background-darker)',
        'design-project': 'var(--background-darker)',
      };
      return subtleColorMap[this.cardType] || 'rgba(0, 134, 230, 0.15)';
    },
    typeLabel() {
      const typeLabelMap = {
        article: 'Writing',
        tool: 'Writing',
        'case-study': 'Case Study',
        'design-project': 'Project',
      };
      return typeLabelMap[this.cardType] || 'Writing';
    },
  },
};
</script>
<style scoped lang="scss">
/* Links and text should inherit global styles, not force color inheritance */
/* * {
  color: inherit;
} */
#textblock {
  display: flex;
  flex-direction: column;
  inline-size: 100%;
}
.eyebrow {
  margin-block-end: 1em;
}

.eyebrow-badge {
  display: inline-block;
  padding: var(--spacing-xxxs) var(--spacing-xs);
  border-radius: var(--spacing-xxxs);
  font-size: var(--font-300);
  --reading-fontWeight: var(--font-300-weight);
  font-weight: var(--font-300-weight);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-block-end: var(--spacing-xs);
}
.title {
  /* flex: 1; */
  inline-size: 100%;
  white-space: normal;
  font-weight: var(--fontWeight-bold);
}

h1.title {
  margin-block-end: var(--spacing-sm);
}

.title-link {
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    text-underline-offset: 0.2em;
    text-decoration-thickness: 0.15rem;
  }
}
.description {
  /* flex: 1; */
  inline-size: 100%;
  white-space: normal;
}
.textblock-align {
  grid-column: 1 / 4;
}
.description {
  margin: var(--spacing-xs) 0 0 0;
}
.textblock--clamped p {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.line-clamp {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}
#btn {
  display: inline-block;
  margin-block-start: var(--spacing-sm);
}
#richlink {
  margin-block-start: var(--spacing-sm) !important;
}
.route {
  margin: var(--spacing-sm) 0 0 0;
}
.textblock-align--left {
  text-align: left;
}
.textblock-align--center {
  text-align: center;
}

.tags {
  margin-block-end: var(--spacing-xs);
  display: flex;
  flex-wrap: wrap;
  column-gap: var(--spacing-xs);
  row-gap: 0;
}

// One line: the meta row (date · read time · tags) never wraps, and whatever
// doesn't fit ends in an ellipsis. Block, not flex, so text-overflow applies;
// the gap moves onto each label.
.tags--content {
  margin-block-start: auto;
  margin-block-end: 0;
  padding-block-start: var(--spacing-xs);
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--foreground-muted);
  font-size: var(--font-2xs); // sizes the ellipsis to match the labels
  --reading-fontWeight: var(--font-2xs-weight);
  font-weight: var(--font-2xs-weight);

  .tag-label:not(:last-child) {
    margin-inline-end: var(--spacing-xs);
  }

  // The global p rule sets white-space back to normal.
  .tag-label p {
    white-space: inherit;
  }
}

.tag-label {
  display: inline;
  cursor: pointer;
  color: var(--foreground-muted);
}

.tag-label p {
  display: inline;
  font-size: var(--font-2xs);
  --reading-fontWeight: var(--font-2xs-weight);
  font-weight: var(--font-2xs-weight);
}

.tag-label--external {
  cursor: default;
  color: var(--foreground-muted);
}

.tag-label--complete {
  cursor: default;
  color: var(--color-success);
}

.tag-label--read-time {
  cursor: default;
  color: var(--foreground-muted);
  display: inline-flex;
  align-items: center;
  gap: 3px;
  // &::after {
  //   content: '|';
  //   color: var(--foreground-muted);

  //   margin-inline-start: var(--spacing-xxs);
  // }

  // &::before {
  //   content: '';
  //   display: inline-block;
  //   width: 16px;
  //   height: 16px;
  //   flex-shrink: 0;
  //   background-color: var(--foreground-muted);
  //   mask-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="black" stroke-width="1.2"/><path d="M6 3v3l1.8 1.3" stroke="black" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>');
  //   -webkit-mask-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="black" stroke-width="1.2"/><path d="M6 3v3l1.8 1.3" stroke="black" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>');
  //   mask-repeat: no-repeat;
  //   -webkit-mask-repeat: no-repeat;
  //   mask-size: contain;
  //   -webkit-mask-size: contain;
  //   mask-position: center;
  //   -webkit-mask-position: center;
  // }
}

/* .tag-label::after {
  content: '•';
  margin-inline-start: var(--spacing-xxs);
  color: var(--text-subtle);
}
.tag-label:last-child::after {
  content: '';
  margin: 0;
} */

/* ------------ BREAKPOINT MD ------------ */
@media only screen and (min-width: 768px) {
  .textblock-align {
    grid-column: auto;
  }
  /* ------------ BREAKPOINT LG ------------ */
  @media only screen and (min-width: 1201px) {
    .textblock-align {
      grid-column: auto;
    }
  }
}
</style>
